import { GridApi, IFilterParams } from 'ag-grid-community';
import { DepartmentListResponse } from 'api/actions/department-list/department-list-response';
import { useApi } from 'api/api-context';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import { Button, Group } from '@mantine/core';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import { useDepartmentText } from 'model/DepartmentTerminology';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import { get } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

const FILTER_KEY = 'department.departmentId';
type Department = DepartmentListResponse[number];

interface ObjectFilterModel {
  filter: string[] | string;
  mode?: 'exact' | 'subtree';
}

type FilterMode = NonNullable<ObjectFilterModel['mode']>;
/** Preserves historical scalar-exact and array-subtree filter models. */
const normalizeModel = (model: ObjectFilterModel | null | undefined): { ids: string[]; mode: FilterMode } => ({
  ids: normalizeIds(model?.filter),
  mode: model?.mode ?? (Array.isArray(model?.filter) ? 'subtree' : 'exact'),
});

/** Accepts current multi-selection models and scalar object/organization deep links. */
const normalizeIds = (value: unknown): string[] => {
  if (value === null || value === undefined || value === '' || value === -1 || value === '-1') return [];
  return Array.from(new Set((Array.isArray(value) ? value : [value]).map(String).filter(Boolean)));
};

/** Avoids reloading preloaded selector rows for repeated grid model notifications. */
const sameIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

/** Matches exact IDs or the union of selected subtrees without changing saved filter intent. */
export const ObjectTreeColumnFilter = forwardRef(
  ({ filterChangedCallback, breadcrumbField }: IFilterParams & { breadcrumbField?: string }, ref) => {
    const model = useRef<{ ids: string[]; mode: FilterMode }>({ ids: [], mode: 'subtree' });
    useImperativeHandle(ref, () => ({
      doesFilterPass({ data }: { data: Record<string, any> }) {
        if (model.current.ids.length === 0) return true;
        const breadcrumbs: Department['breadcrumbs'] =
          (breadcrumbField && get(data, breadcrumbField)) ??
          get(data, 'department.breadcrumbs') ??
          get(data, 'departmentBreadcrumbs') ??
          get(data, 'breadcrumbs') ??
          [];
        if (model.current.mode === 'exact') {
          const id = get(data, FILTER_KEY) ?? breadcrumbs.at(-1)?.departmentId;
          return model.current.ids.includes(String(id));
        }
        return breadcrumbs.some(({ departmentId }) => model.current.ids.includes(String(departmentId)));
      },
      getModel: () =>
        model.current.ids.length
          ? {
              filter: model.current.mode === 'exact' ? model.current.ids[0] : model.current.ids,
              mode: model.current.mode,
            }
          : null,
      setModel: (nextModel: ObjectFilterModel | null) => {
        model.current = normalizeModel(nextModel);
      },
      isFilterActive: () => model.current.ids.length > 0,
      onFloatingFilterChanged(nextModel: ObjectFilterModel | null) {
        model.current = normalizeModel(nextModel);
        filterChangedCallback();
      },
    }));
    return null;
  }
);

interface FloatingFilterProps {
  api: GridApi;
  onChange?: (filters: Record<string, any>) => void;
  filterModel?: Record<string, any>;
  organizationFilterKey: string;
  permissionSlug?: string;
}

/** AG Grid remains the owner of the serializable filter model and option scope. */
export const ObjectTreeFloatingFilter = forwardRef(
  ({ api, onChange, filterModel, organizationFilterKey, permissionSlug }: FloatingFilterProps, ref) => {
    const { getAction } = useApi();
    const departmentText = useDepartmentText();
    const { mode: uiMode } = useDepartmentUiMode();
    const [selection, setSelection] = useState(() => normalizeModel((filterModel ?? api.getFilterModel())[FILTER_KEY]));
    const [replacing, setReplacing] = useState(false);
    const [organizationIds, setOrganizationIds] = useState(() =>
      normalizeIds((filterModel ?? api.getFilterModel())[organizationFilterKey]?.filter)
    );
    const [departments, setDepartments] = useState<DepartmentListResponse | null>(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
      let active = true;
      setDepartments(null);
      setFailed(false);
      getAction('DepartmentList')({
        query: { filters: { 'permissionSlug.eq': permissionSlug, 'showDiscarded.eq': 1 } },
      })
        .then((result) => {
          if (active) setDepartments(result);
        })
        .catch((error) => {
          if (active) {
            setFailed(true);
            panic(error);
          }
        });
      return () => {
        active = false;
      };
    }, [getAction, permissionSlug, attempt]);

    const syncFromGrid = useCallback(() => {
      const currentModel = filterModel ?? api.getFilterModel();
      const nextSelection = normalizeModel(currentModel[FILTER_KEY]);
      const nextOrganizations = normalizeIds(currentModel[organizationFilterKey]?.filter);
      setSelection((previous) =>
        previous.mode === nextSelection.mode && sameIds(previous.ids, nextSelection.ids) ? previous : nextSelection
      );
      setOrganizationIds((previous) => (sameIds(previous, nextOrganizations) ? previous : nextOrganizations));
    }, [api, filterModel, organizationFilterKey]);

    useImperativeHandle(ref, () => ({ onParentModelChanged: syncFromGrid }), [syncFromGrid]);
    useEffect(syncFromGrid, [syncFromGrid]);
    useEffect(() => {
      api.addEventListener('filterChanged', syncFromGrid);
      return () => api.removeEventListener('filterChanged', syncFromGrid);
    }, [api, syncFromGrid]);

    const pushSelection = useCallback(
      (ids: string[], mode: FilterMode) => {
        setSelection({ ids, mode });
        setReplacing(false);
        const nextModel = ids.length ? { filter: mode === 'exact' ? ids[0] : ids, mode } : null;
        if (onChange) onChange({ [FILTER_KEY]: nextModel });
        else api.setFilterModel({ ...api.getFilterModel(), [FILTER_KEY]: nextModel });
      },
      [api, onChange]
    );

    const available = useMemo(
      () =>
        (departments ?? []).filter(({ organization }) =>
          organizationIds.length ? organizationIds.includes(String(organization.organizationId)) : true
        ),
      [departments, organizationIds]
    );

    const currentMode: FilterMode = uiMode === 'legacy' ? 'exact' : 'subtree';
    const availableById = useMemo(
      () => new Map(available.map((department) => [String(department.departmentId), department.departmentName])),
      [available]
    );
    const modeMismatch = selection.ids.length > 0 && selection.mode !== currentMode;
    const unavailableSelection = departments !== null && selection.ids.some((id) => !availableById.has(id));
    if ((modeMismatch || unavailableSelection) && !replacing) {
      const names = selection.ids.map((id) => availableById.get(id) ?? `ID ${id}`).join(', ');
      return (
        <Group spacing={4} noWrap>
          <P2Regular>{`${names} · ${selection.mode === 'exact' ? 'Presne' : 'Vrátane podstromu'}`}</P2Regular>
          <Button size="xs" variant="subtle" onClick={() => setReplacing(true)}>
            Nahradiť
          </Button>
          <Button size="xs" variant="subtle" onClick={() => pushSelection([], currentMode)}>
            Zrušiť
          </Button>
        </Group>
      );
    }
    if (uiMode === 'legacy' && departments === null) {
      return failed ? (
        <Button size="xs" onClick={() => setAttempt((current) => current + 1)}>
          Skúsiť znova
        </Button>
      ) : (
        <P2Regular>Načítavam strediská…</P2Regular>
      );
    }

    return uiMode === 'legacy' ? (
      <DepartmentSelect
        clearable
        groupByOrganization
        size="sm"
        w="100%"
        style={{ alignSelf: 'flex-start' }}
        aria-label="Filtrovať strediská"
        value={selection.mode === 'exact' && !replacing ? selection.ids[0] ?? null : null}
        onChange={(id) => pushSelection(id ? [String(id)] : [], 'exact')}
        onDropdownClose={() => setReplacing(false)}
        preloadedDepartments={available}
        placeholder="Všetky strediská"
      />
    ) : (
      <DepartmentSelect
        multiple
        selectSubtrees
        popover
        confirmSelection={false}
        showSelectionCount
        withinPortal={!onChange}
        groupByOrganization
        aria-label={departmentText('Filtrovať položky úrovne', 'Filtrovať strediská')}
        value={selection.mode === 'subtree' && !replacing ? selection.ids : []}
        onChange={(ids) => pushSelection(ids, 'subtree')}
        onPickerClose={() => setReplacing(false)}
        preloadedDepartments={available}
        loading={departments === null && !failed}
        loadError={failed}
        loadingPlaceholder={departmentText('Načítavam položky úrovne…', 'Načítavam strediská…')}
        emptyText={departmentText('Žiadne položky úrovne', 'Žiadne strediská')}
        errorText={departmentText('Položky úrovne sa nepodarilo načítať.', 'Strediská sa nepodarilo načítať.')}
        onRetry={() => setAttempt((current) => current + 1)}
      />
    );
  }
);
